//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Close, Checkmark } from '@carbon/icons-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { GroupFormData, IntegrationOption, UserGroupDto } from './types';

interface AddGroupSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: GroupFormData) => Promise<void>;
  integrations: IntegrationOption[];
  users: Array<{ id: string; name: string; email: string }>;
  allGroups: UserGroupDto[];
  editingGroupId?: string | null;
  editGroup?: GroupFormData | null;
}

const AddGroupSidebar: React.FC<AddGroupSidebarProps> = ({
  isOpen,
  onClose,
  onSave,
  integrations,
  users,
  allGroups,
  editingGroupId,
  editGroup,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    description: '',
    selectedIntegrationId: '',
    allowUserOverride: false,
    selectedUserIds: [],
  });
  const [initialFormData, setInitialFormData] = useState<GroupFormData | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; save?: string }>({});
  const [specialCharWarning, setSpecialCharWarning] = useState('');
  const specialCharTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Map of user IDs to every OTHER group they already belong to in this teamspace.
  //
  // A user may belong to any number of groups, so this is shown as context — "who is
  // already where" — and never as a restriction. It used to hold a single group name and
  // gate selection; that mirrored a backend constraint which no longer exists.
  const userGroupMap = useMemo(() => {
    const map = new Map<string, string[]>();

    allGroups.forEach((group) => {
      // Skip the current group if we're editing
      if (editingGroupId && group.id === editingGroupId) {
        return;
      }

      group.userIds.forEach((userId) => {
        map.set(userId, [...(map.get(userId) ?? []), group.name]);
      });
    });

    return map;
  }, [allGroups, editingGroupId]);

  // Check if form has changed from initial values
  const hasFormChanged = useMemo(() => {
    if (!initialFormData) return true; // New group - always allow save

    // For editing, check if any field changed
    const descriptionChanged =
      formData.description !== initialFormData.description;
    const integrationChanged =
      formData.selectedIntegrationId !== initialFormData.selectedIntegrationId;
    const usersChanged =
      JSON.stringify([...formData.selectedUserIds].sort()) !==
      JSON.stringify([...initialFormData.selectedUserIds].sort());

    return descriptionChanged || integrationChanged || usersChanged;
  }, [formData, initialFormData]);

  useEffect(() => {
    if (editGroup && isOpen) {
      setFormData(editGroup);
      setInitialFormData(editGroup);
      setCurrentStep(1);
    } else if (isOpen) {
      const newFormData = {
        name: '',
        description: '',
        selectedIntegrationId: '',
        allowUserOverride: false,
        selectedUserIds: [],
      };
      setFormData(newFormData);
      setInitialFormData(newFormData);
      setCurrentStep(1);
    }
  }, [editGroup, isOpen]);

  // Cleanup timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (specialCharTimerRef.current) {
        clearTimeout(specialCharTimerRef.current);
      }
    };
  }, []);

  // Inline validation for duplicate group names (similar to Loop/Teamspace)
  // Uses debouncing to check after user stops typing for 300ms
  useEffect(() => {
    // Skip validation if editing (name field is disabled) or if name is empty
    if (editGroup || !formData.name.trim()) {
      setErrors((prev) => ({ ...prev, name: undefined }));
      return;
    }

    // Debounce the duplicate check: wait 300ms after user stops typing
    const timeoutId = setTimeout(() => {
      // Check for duplicate name (case-insensitive)
      const isDuplicate = allGroups.some(
        (group) =>
          group.name.toLowerCase() === formData.name.trim().toLowerCase() &&
          group.id !== editingGroupId,
      );

      if (isDuplicate) {
        setErrors((prev) => ({
          ...prev,
          name: t('userGroups.groupNameExists'),
        }));
      } else {
        setErrors((prev) => ({ ...prev, name: undefined }));
      }
    }, 300);

    // Cleanup: cancel the timeout if user types again before 300ms
    return () => clearTimeout(timeoutId);
  }, [formData.name, allGroups, editingGroupId, editGroup]);

  const validateStep1 = (): boolean => {
    // Check if name is empty
    if (!formData.name.trim()) {
      setErrors((prev) => ({
        ...prev,
        name: t('userGroups.groupNameRequired'),
      }));
      return false;
    }

    // If there's already a name error (from inline validation), validation fails
    if (errors.name) {
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSave = async () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }

    try {
      setSaving(true);
      setErrors({});
      await onSave(formData);
      handleClose();
    } catch (error: any) {
      console.error('Error saving group:', error);
      // Handle backend validation errors
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        t('userGroups.errors.saveFailed');
      setErrors({ save: errorMessage });
      // Stay on current step to show error
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      name: '',
      description: '',
      selectedIntegrationId: '',
      allowUserOverride: false,
      selectedUserIds: [],
    });
    setErrors({});
    onClose();
  };

  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow only letters, numbers, and underscores (no special characters or spaces)
    if (!/^[a-zA-Z0-9_]*$/.test(input)) {
      // Show transient warning but don't update the field value
      setSpecialCharWarning(t('userGroups.specialCharactersNotAllowed'));
      // Clear any previous timer to avoid race conditions on rapid input
      if (specialCharTimerRef.current) {
        clearTimeout(specialCharTimerRef.current);
      }
      // Auto-clear the warning after 2 seconds
      specialCharTimerRef.current = setTimeout(() => {
        setSpecialCharWarning('');
        specialCharTimerRef.current = null;
      }, 2000);
      return; // 🚨 Do not update formData, keep last valid value
    }

    // Clear special char warning if input is valid
    setSpecialCharWarning('');

    // Update form data with valid input
    setFormData({ ...formData, name: input });
  };

  const toggleUser = (userId: string) => {
    // Membership in other groups is no longer a reason to block selection.
    if (formData.selectedUserIds.includes(userId)) {
      setFormData({
        ...formData,
        selectedUserIds: formData.selectedUserIds.filter((id) => id !== userId),
      });
    } else {
      setFormData({
        ...formData,
        selectedUserIds: [...formData.selectedUserIds, userId],
      });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 top-12 z-40 bg-black/20 dark:bg-black/40"
        onClick={handleClose}
        data-testid="add-group-sidebar-backdrop"
      />

      {/* Sidebar */}
      <div
        className="fixed bottom-0 right-0 top-12 z-50 flex w-[526px] flex-col bg-white shadow-xl dark:bg-[#262626]"
        data-testid="add-group-sidebar"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 p-4 dark:border-gray-700 dark:bg-[#393939]">
          <h2 className="truncate text-base font-semibold text-gray-700 dark:text-white">
            {editGroup
              ? t('userGroups.editGroup', { name: editGroup.name })
              : t('userGroups.addGroup')}
          </h2>
          <button
            onClick={handleClose}
            className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-700"
            data-testid="add-group-sidebar-close"
            aria-label="Close sidebar"
          >
            <Close size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="border-b border-gray-200 px-6 py-6 dark:border-gray-700">
          <div className="flex items-center gap-4">
            {/* Step 1 */}
            <div className="relative flex-1">
              <div
                className={`absolute left-0 right-0 top-0 h-1 rounded-full ${
                  currentStep >= 1 ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
              <div className="flex items-center gap-2 pt-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                    currentStep > 1
                      ? 'bg-blue-500 text-white'
                      : currentStep === 1
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-300 text-gray-700'
                  }`}
                >
                  {currentStep > 1 ? <Checkmark size={16} /> : '1'}
                </div>
                <span
                  className={`whitespace-nowrap text-sm ${
                    currentStep >= 1
                      ? 'font-medium text-gray-900'
                      : 'text-gray-500'
                  }`}
                >
                  {t('userGroups.configuration')}
                </span>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative flex-1">
              <div
                className={`absolute left-0 right-0 top-0 h-1 rounded-full ${
                  currentStep >= 2 ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
              <div className="flex items-center gap-2 pt-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                    currentStep === 2
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-300 text-gray-700'
                  }`}
                >
                  2
                </div>
                <span
                  className={`whitespace-nowrap text-sm ${
                    currentStep >= 2
                      ? 'font-medium text-gray-900'
                      : 'text-gray-500'
                  }`}
                >
                  {t('userGroups.selectMembers')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="add-group-step-1">
              {/* Error Message */}
              {errors.save && (
                <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
                  <p className="font-medium">
                    {t('userGroups.errorSavingGroup')}
                  </p>
                  <p className="mt-1">{errors.save}</p>
                </div>
              )}

              {/* No Integrations Warning */}
              {integrations.length === 0 && (
                <div className="rounded-md bg-yellow-50 p-4 text-sm text-yellow-800">
                  <p className="font-medium">
                    {t('userGroups.noIntegrationsAvailable')}
                  </p>
                  <p className="mt-1">
                    {t('userGroups.noIntegrationsMessage')}
                  </p>
                </div>
              )}

              {/* Group Name */}
              <div>
                <Label htmlFor="group-name" className="mb-2">
                  {t('userGroups.groupName')} *
                </Label>
                <Input
                  id="group-name"
                  type="text"
                  value={formData.name}
                  onChange={handleGroupNameChange}
                  placeholder={t('userGroups.enterGroupName')}
                  className={errors.name ? 'border-red-500' : ''}
                  disabled={!!editGroup}
                  maxLength={20}
                  data-testid="group-name-input"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.name.length}/20 characters
                </p>
                {specialCharWarning && (
                  <p className="mt-1 text-xs italic text-red-600 dark:text-red-400">
                    {specialCharWarning}
                  </p>
                )}
                {editGroup && (
                  <p className="mt-1 text-xs italic text-amber-600 dark:text-amber-400">
                    {t('userGroups.groupNameCannotEdit')}
                  </p>
                )}
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="group-description" className="mb-2">
                  {t('userGroups.description')}
                </Label>
                <Textarea
                  id="group-description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder={t('userGroups.enterGroupDescription')}
                  rows={3}
                  maxLength={100}
                  data-testid="group-description-input"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.description.length}/100 characters
                </p>
              </div>

              {/* Integration Assignment */}
              <div>
                <Label htmlFor="integration-select" className="mb-1">
                  {t('userGroups.integrationAssignment')}
                </Label>
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  {t('userGroups.integrationAssignmentHelp')}
                </p>
                <Select
                  value={formData.selectedIntegrationId}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      selectedIntegrationId: value,
                    })
                  }
                >
                  <SelectTrigger
                    id="integration-select"
                    data-testid="integration-select"
                  >
                    <SelectValue
                      placeholder={t('userGroups.selectIntegration')}
                    />
                  </SelectTrigger>
                  <SelectContent className="w-[var(--radix-select-trigger-width)]">
                    {integrations.map((integration) => (
                      <SelectItem
                        key={integration.id}
                        value={integration.id}
                        className="overflow-hidden"
                      >
                        <span className="block truncate">
                          {integration.name}
                          {integration.isEnabled === false && (
                            // Selectable, but flagged: a group assigned a disabled
                            // integration grants its members no AI access at all until
                            // someone switches it on.
                            <span
                              className="ml-2 text-xs opacity-70"
                              data-testid={`integration-disabled-${integration.id}`}
                            >
                              {t('userGroups.integrationDisabled') ||
                                '(disabled)'}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Allow User Override - Only show when creating new group */}
              {!editingGroupId && (
                <div className="flex items-center justify-between rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-700 dark:bg-[#393939]">
                  <div className="flex-1">
                    <Label htmlFor="allow-override" className="font-semibold">
                      {t('userGroups.allowUserOverride')}
                    </Label>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('userGroups.allowUserOverrideHelp')}
                    </p>
                  </div>
                  <Switch
                    id="allow-override"
                    checked={formData.allowUserOverride}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, allowUserOverride: checked })
                    }
                    data-testid="allow-override-switch"
                  />
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4" data-testid="add-group-step-2">
              <div className="mb-4">
                <p className="text-sm text-gray-700">
                  {t('userGroups.selectedCount', {
                    count: formData.selectedUserIds.length,
                  })}
                </p>
              </div>

              {/* User List */}
              <div className="space-y-2">
                {users.map((user) => {
                  const isSelected = formData.selectedUserIds.includes(user.id);
                  const otherGroups = userGroupMap.get(user.id) ?? [];

                  return (
                    <div
                      key={user.id}
                      onClick={() => toggleUser(user.id)}
                      className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
                        isSelected
                          ? 'cursor-pointer border-blue-500 bg-blue-50'
                          : 'cursor-pointer border-gray-300 bg-white hover:bg-gray-50'
                      }`}
                      data-testid={`user-item-${user.id}`}
                      title={
                        otherGroups.length > 0
                          ? t('userGroups.alsoInGroups', {
                              groupNames: otherGroups.join(', '),
                            })
                          : ''
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300 text-sm font-medium text-gray-700">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {user.email}
                            {otherGroups.length > 0 && (
                              // Context, not a warning: existing membership elsewhere is
                              // expected now, so this is rendered in a neutral tone rather
                              // than the orange it used when it marked the user unusable.
                              <span
                                className="ml-2 text-xs text-gray-500"
                                data-testid={`user-other-groups-${user.id}`}
                              >
                                •{' '}
                                {t('userGroups.alsoInGroups', {
                                  groupNames: otherGroups.join(', '),
                                })}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 p-4">
          <div>
            {currentStep === 2 && (
              <Button
                variant="ghost"
                onClick={handleBack}
                data-testid="add-group-back-button"
              >
                {t('userGroups.back')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              data-testid="add-group-cancel-button"
            >
              {t('userGroups.cancel')}
            </Button>
            {currentStep === 1 ? (
              <Button
                onClick={handleNext}
                disabled={integrations.length === 0 || !!errors.name}
                data-testid="add-group-next-button"
              >
                {t('userGroups.next')}
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                disabled={saving || (editingGroupId ? !hasFormChanged : false)}
                data-testid="add-group-save-button"
              >
                {saving ? t('userGroups.saving') : t('userGroups.save')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddGroupSidebar;
